import api from './axiosConfig';

export interface HVAAgent {
  id: number;
  name: string;
  bxpname: string;
  description: string;
  language: string;
  agentcode: string;
}

export interface HVAPOC {
  id: number;
  pocgroup: string;
  agentcode: string;
  picture: string;
  title: string;
  description: string;
  firstmessage: string;
  prompt: string;
  voice: string;
  language: string;
  flag: string;
  name: string;
  pocform?: number;
}

// *************** CONVERSATIONAL ENGINE *******************
export interface ConversationalEngineAgent {
  agentid: string;
  clientowner: string;
  testtitle: string;
  windowstyle: number;
  agentname: string;
  bxpname: string;
  description: string;
  firstmessage: string;
  inboundfirstmessage: string;
  prompt: string;
  inboundprompt: string;
  voice: string;
  language: string;
  sttConfigId?: number | null;
  llmConfigId?: number | null;
  ttsConfigId?: number | null;
  summaryConfigId?: number | null;
  flag: string;
  active: boolean;
  environment: 'Development' | 'POC' | 'Staging' | 'Production';
}

export interface ConversationalEngineCampaignAgent {
  // Campos del agente
  agentid: string;
  clientowner: string;
  testtitle: string;
  windowstyle: number;
  agentname: string;
  bxpname: string;
  description: string;
  firstmessage: string;
  inboundfirstmessage: string;
  prompt: string;
  inboundprompt: string;
  voice: string;
  language: string;
  sttadapter: string;
  llmadapter: string;
  ttsadapter: string;
  flag: string;
  active: boolean;
  // Campos de la campaña
  campaignId: number;
  campaignName: string;
  campaignServiceId: number;
  campaignType: number;
  campaignDescription: string;
  bxpCampaignId: number;
}

export interface StartCallRequest {
  bxpCampaignID: number; // Se convertirá en bxpcampaignid en el backend
  agentId?: string;
  connid?: string; // Opcional, ya que el backend puede generarlo
  importid: string;
  recordid: string;
  phonenumber: string; // Requerido según la API
  calltype?: string; // 'Outbound' o 'Inbound'
  companyname?: string;
  customername?: string;
  customertrn?: string;
  customerreference?: string;
  customerdob?: string; // Formato de fecha como string 'YYYY-MM-DD'
  customeraddress?: string;
  customeremail?: string;
  customeriscompany?: number;
  customerfathername?: string;
  // eslint-disable-next-line
  callcontext?: Record<string, any>; // Contexto adicional
  // eslint-disable-next-line
  bxpcontext?: Record<string, any>; // Contexto BXP
}

export interface StartCallResponse {
  callId: string;
  agentid: string;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
  agentaiadapters?: {
    sttadapter: string;
    llmadapter: string;
    ttsadapter: string;
  };
  tools: object[]; // Herramientas asignadas al agente
}

export interface CallConnectedRequest {
  agentId: string;
  callId: string;
  conversationid: string;
}

export interface CallConnectedResponse {
  success: boolean;
}

export interface EndCallRequest {
  connid: string;
  telephonyresult: string;
}

export interface EndCallResponse {
  success: boolean;
  message: string;
  data?: {
    connid: string;
    phonenumber?: string;
    state: string;
    telephonyresult: string;
    businessresult?: string;
    rpc: boolean;
    positiveresult: boolean;
    summary?: string;
    startTime: string;
    endTime: string;
    duration: number;
    transcription: Array<{
      timestamp: string;
      speaker: string;
      text: string;
    }>;
  };
}

export interface ToolRequest {
  callId: string;
  tool: string;
  parameters: {
    // eslint-disable-next-line
    [key: string]: any; // Para parámetros opcionales
  };
}

export interface ToolResponse {
  success: boolean;
  error?: string;
  message?: string;
  // eslint-disable-next-line
  data?: any;
  pendingValidations?: string[];
}

// Interfaces para las herramientas y validaciones
export interface AgentTool {
  toolid: string;
  name: string;
  description: string;
  toolgroup: string;
  active: boolean;
  requiredvalidations?: string[];
}

export interface ToolConfig {
  toolid: string;
  calltype?: 'Outbound' | 'Inbound';
  requiredvalidations: string[];
}

export interface Validation {
  validationid: string;
  name: string;
  description: string;
  active: boolean;
}

export interface AgentSampleDataResponse {
  hasSampleData: boolean;
  sampleData?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    baseSampleData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callContextSample: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bxpContextSample: any;
  };
}

// Interfaces para los adaptadores
export interface IAAdapter {
  id: number;
  adapterType: 'STT' | 'LLM' | 'TTS';
  adapterCode: string;
  adapterName: string;
  provider: string;
}

export interface GroupedAdapters {
  STT: IAAdapter[];
  LLM: IAAdapter[];
  TTS: IAAdapter[];
}

export interface AgentAdaptersConfig {
  language: string;
  voice: string;
  sttAdapter: string;
  llmAdapter: string;
  ttsAdapter: string;
}

// Nuevas configuraciones IA para agentes
export interface AIAdapterConfig {
  id: number;
  configName: string;
  description?: string;
  adapterCode: string;
  active: boolean;

  // Helper para diferenciar proveedores en el frontend
  sttProvider?: string;
  ttsProvider?: string;

  // Campos comunes
  apiKey?: string;
  endpoint?: string;

  // STT específicos - Azure Speech
  azureKey?: string;
  region?: string;
  model?: string;
  deploymentName?: string;
  apiVersion?: string;
  language?: string;
  profanityOption?: string;
  enableAudioLogging?: boolean;
  enableDictation?: boolean;
  silenceTimeoutMs?: number;
  maxSilenceTimeoutMs?: number;
  supportedLanguages?: string;
  autoDetectLanguage?: boolean;

  // STT específicos - OpenAI Whisper
  audioFormat?: string;
  sampleRate?: number;
  noiseReduction?: string;
  transcribePrompt?: string;
  vadThreshold?: number;
  vadPrefixPadding?: number;
  vadSilenceDuration?: number;

  // LLM específicos
  deployment?: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;

  // TTS específicos
  baseUrl?: string;
  voiceId?: string;
  defaultVoiceId?: string;
  voice?: string;
  defaultVoice?: string;
  outputFormat?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  speakerBoost?: boolean;
  speed?: number;
  useSsml?: boolean;
  languageCode?: string;
  textNormalization?: string;
  inactivityTimeout?: number;
  syncAlignment?: boolean;
  autoMode?: boolean;
  enableLogging?: boolean;
  streamingEndpoint?: string;
  streamingEnabled?: boolean;
}

export type AdapterType = 'stt' | 'llm' | 'tts' | 'summary';

export interface SignedUrlResponse {
  signed_url: string;
}

export interface AgentEnvironmentResponse {
  success: boolean;
  role: string;
  allowedEnvironments: string[];
  agents: {
    Development: ConversationalEngineAgent[];
    POC: ConversationalEngineAgent[];
    Staging: ConversationalEngineAgent[];
    Production: ConversationalEngineAgent[];
  };
}

export interface ChangeEnvironmentRequest {
  newEnvironment: string;
  reason?: string;
  campaignId?: number;
}

// ********************************************************

export const getAllHVAAgents = async (): Promise<HVAAgent[]> => {
  try {
    const response = await api.get('hvaagents');
    return response.data;
  } catch (error) {
    console.error('Error fetching Agents:', error);
    return [];
  }
};

export const getHVASignedUrl = async (agentId: string): Promise<string> => {
  try {
    const response = await api.get<SignedUrlResponse>('convhva/signed-url', {
      params: { agentId }
    });

    return response.data.signed_url;
  } catch (error) {
    console.error('Error fetching signed URL:', error);
    throw error;
  }
};

export const getPOCsByGroup = async (group: string): Promise<HVAPOC[]> => {
  const response = await api.get(`hvaagents/pocgroup/${group}`);
  return response.data;
};

// *********** Conversational Engine *************************************

// Interfaces para el sistema de diagramas
export interface DiagramResponse {
  success: boolean;
  diagram: string;
  prompt_type: 'outbound' | 'inbound';
  cached?: boolean;
  generated_at?: Date;
  generated_by?: string;
}

export interface GenerateDiagramRequest {
  force?: boolean;
  type?: 'outbound' | 'inbound';
}

export interface UploadDiagramRequest {
  mermaidCode: string;
  promptType?: 'outbound' | 'inbound';
}

// Interfaces para recuperar clientes de las tablas CT (RealCustomer)
export interface RealCustomer {
  id: string;
  customername: string;
  customerphone: string;
  customertrn: string;
  customerreference?: string;
  customerdob?: string;
  customerfathername?: string;
  customeraddress?: string;
  customeremail?: string;
  customeriscompany?: number;
  companyname?: string;
  importid?: string;
  recordid?: string;
  // Contextos adicionales
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callcontext?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bxpcontext?: Record<string, any>;
}

export interface PaginatedCustomersResponse {
  customers: RealCustomer[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetRealCustomersParams {
  page?: number;
  pageSize?: number;
  search?: string;
}
// *******************************************************************

export const getConversationalEngineAgentsByOwner = async (
  clientowner: string
): Promise<ConversationalEngineAgent[]> => {
  const response = await api.get(
    `hvaagents/conversationalengine/${clientowner}`
  );
  return response.data;
};

export const getConversationalEngineCampaigns = async (): Promise<
  ConversationalEngineCampaignAgent[]
> => {
  const response = await api.get(`hvaagents/conversationalenginecampaigns`);
  return response.data;
};

export const conversationalEngineStartCall = async (
  request: StartCallRequest
): Promise<StartCallResponse> => {
  try {
    const callData = {
      ...request,
      calltype: request.calltype || 'Outbound'
    };

    const response = await api.post(
      'hvaagents/conversationalengine/callstart',
      callData
    );
    return response.data;
  } catch (error) {
    console.error('Error starting call:', error);
    throw error;
  }
};

export const conversationalEngineNotifyCallConnected = async (
  request: CallConnectedRequest
): Promise<CallConnectedResponse> => {
  try {
    const response = await api.post(
      'hvaagents/conversationalengine/callconnected',
      request
    );
    return response.data;
  } catch (error) {
    console.error('Error notifying call connection:', error);
    throw error;
  }
};

export const conversationalEngineEndCall = async (
  request: EndCallRequest
): Promise<EndCallResponse> => {
  try {
    const response = await api.post(
      'hvaagents/conversationalengine/callend',
      request
    );
    return response.data;
  } catch (error) {
    console.error('Error ending call:', error);
    throw error;
  }
};

export const conversationalEngineToolRequest = async (
  request: ToolRequest
): Promise<ToolResponse> => {
  try {
    const response = await api.post(
      'hvaagents/conversationalengine/tool',
      request
    );
    return response.data;
  } catch (error) {
    console.error('Error executing tool:', error);
    throw error;
  }
};

// obtenemos los datos de un agente conversacional
export const getAgentData = async (
  agentId: string
): Promise<{
  agentid: string;
  firstmessage: string;
  inboundfirstmessage: string;
  prompt: string;
  inboundprompt: string;
  callresultprompt: string;
  voice: string;
  language: string;
  sttConfigId?: number | null;
  llmConfigId?: number | null;
  ttsConfigId?: number | null;
  summaryConfigId?: number | null;
}> => {
  try {
    const response = await api.get(`/hvaagents/agent/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agent data:', error);
    throw error;
  }
};

// Obtener adaptadores disponibles
export const getAvailableIAAdapters = async (): Promise<GroupedAdapters> => {
  try {
    const response = await api.get('/hvaagents/iaadapters');
    return response.data;
  } catch (error) {
    console.error('Error fetching available IA adapters:', error);
    throw error;
  }
};

// Obtener configuración de adaptadores del agente
export const getAgentAdaptersConfig = async (
  agentId: string
): Promise<AgentAdaptersConfig> => {
  try {
    const response = await api.get(`/hvaagents/adaptersconfig/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agent adapters config:', error);
    throw error;
  }
};

// Actualizar configuración de adaptadores del agente
export const updateAgentAdaptersConfig = async (
  agentId: string,
  config: Partial<AgentAdaptersConfig>
): Promise<void> => {
  try {
    await api.put(`/hvaagents/adaptersconfig/${agentId}`, config);
  } catch (error) {
    console.error('Error updating agent adapters config:', error);
    throw error;
  }
};

// Obtiene las herramientas asignadas a un agente específico
export const getAgentTools = async (
  agentId: string,
  calltype: 'Outbound' | 'Inbound'
): Promise<AgentTool[]> => {
  try {
    const response = await api.get(
      `/hvaagents/agenttools/${agentId}/${calltype}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching agent tools:', error);
    throw error;
  }
};

// Obtiene todas las herramientas disponibles en el sistema
export const getAvailableTools = async (): Promise<AgentTool[]> => {
  try {
    const response = await api.get('/hvaagents/tools');
    return response.data;
  } catch (error) {
    console.error('Error fetching available tools:', error);
    throw error;
  }
};

// Obtiene todas las validaciones disponibles en el sistema
export const getAgentValidations = async (): Promise<Validation[]> => {
  try {
    const response = await api.get('/hvaagents/validations');
    return response.data;
  } catch (error) {
    console.error('Error fetching validations:', error);
    throw error;
  }
};

// Actualiza el mensaje inicial del agente
export const updateAgentFirstMessage = async (
  agentId: string,
  firstMessage: string
): Promise<void> => {
  try {
    await api.put(`/hvaagents/firstmessage/${agentId}`, { firstMessage });
  } catch (error) {
    console.error('Error updating Outbound Greeting:', error);
    throw error;
  }
};

// Actualiza el mensaje inicial del agente
export const updateAgentInboundFirstMessage = async (
  agentId: string,
  inboundFirstMessage: string
): Promise<void> => {
  try {
    await api.put(`/hvaagents/inboundfirstmessage/${agentId}`, {
      inboundFirstMessage
    });
  } catch (error) {
    console.error('Error updating Inbound Greeting:', error);
    throw error;
  }
};

// Actualiza el prompt (instrucciones) del agente
export const updateAgentPrompt = async (
  agentId: string,
  prompt: string
): Promise<void> => {
  try {
    await api.put(`/hvaagents/prompt/${agentId}`, { prompt });
  } catch (error) {
    console.error('Error updating prompt:', error);
    throw error;
  }
};

// Actualiza el prompt de inbound del agente
export const updateAgentInboundPrompt = async (
  agentId: string,
  inboundprompt: string
): Promise<void> => {
  try {
    await api.put(`/hvaagents/inboundprompt/${agentId}`, { inboundprompt });
  } catch (error) {
    console.error('Error updating inbound prompt:', error);
    throw error;
  }
};

// Actualiza el callresultprompt
export const updateAgentCallResultPrompt = async (
  agentId: string,
  callresultprompt: string
): Promise<void> => {
  try {
    await api.put(`/hvaagents/callresultprompt/${agentId}`, {
      callresultprompt
    });
  } catch (error) {
    console.error('Error updating call result prompt:', error);
    throw error;
  }
};

// Actualiza la configuración de herramientas del agente
export const updateAgentToolConfig = async (
  agentId: string,
  calltype: 'Outbound' | 'Inbound',
  toolConfigs: ToolConfig[]
): Promise<void> => {
  try {
    await api.put(`/hvaagents/toolconfig/${agentId}`, {
      calltype,
      toolConfigs
    });
  } catch (error) {
    console.error('Error updating tool configuration:', error);
    throw error;
  }
};

// Verifica si una herramienta está asignada a un agente
export const checkToolAssignment = async (
  agentId: string,
  toolId: string
): Promise<boolean> => {
  try {
    const response = await api.get(`/hvaagents/checktool/${agentId}/${toolId}`);
    return response.data.assigned;
  } catch (error) {
    console.error('Error checking tool assignment:', error);
    return false;
  }
};

// Asigna una herramienta a un agente
export const assignToolToAgent = async (
  agentId: string,
  toolId: string,
  calltype: 'Outbound' | 'Inbound',
  requiredValidations: string[] = []
): Promise<void> => {
  try {
    await api.post(`/hvaagents/assigntool`, {
      agentId,
      toolId,
      calltype,
      requiredValidations
    });
  } catch (error) {
    console.error('Error assigning tool to agent:', error);
    throw error;
  }
};

// Elimina una herramienta de un agente
export const removeToolFromAgent = async (
  agentId: string,
  calltype: 'Outbound' | 'Inbound',
  toolId: string
): Promise<void> => {
  try {
    await api.delete(`/hvaagents/removetool/${agentId}/${calltype}/${toolId}`);
  } catch (error) {
    console.error('Error removing tool from agent:', error);
    throw error;
  }
};

// Actualiza las validaciones requeridas para una herramienta de un agente
export const updateToolValidations = async (
  agentId: string,
  calltype: 'Outbound' | 'Inbound',
  toolId: string,
  requiredValidations: string[]
): Promise<void> => {
  try {
    await api.put(`/hvaagents/toolvalidations`, {
      agentId,
      calltype,
      toolId,
      requiredValidations
    });
  } catch (error) {
    console.error('Error updating tool validations:', error);
    throw error;
  }
};

// Obtener datos de ejemplo del agente
export const getAgentSampleData = async (
  agentId: string
): Promise<AgentSampleDataResponse> => {
  try {
    const response = await api.get(`/hvaagents/sampledata/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agent sample data:', error);
    throw error;
  }
};

// Actualizar datos de ejemplo del agente
export const updateAgentSampleData = async (
  agentId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sampleData: any
): Promise<void> => {
  try {
    await api.put(`/hvaagents/sampledata/${agentId}`, { sampleData });
  } catch (error) {
    console.error('Error updating agent sample data:', error);
    throw error;
  }
};

// Obtener todos los agentes agrupados por entorno
export const getAllAgentsByEnvironment =
  async (): Promise<AgentEnvironmentResponse> => {
    try {
      const response = await api.get('/hvaagents/agents/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching agents by environment:', error);
      throw error;
    }
  };

// Obtener un agente por su ID
export const getAgentById = async (
  agentId: string
): Promise<ConversationalEngineAgent> => {
  try {
    const response = await api.get(`/hvaagents/agents/${agentId}`);
    return response.data.agent;
  } catch (error) {
    console.error('Error fetching agent:', error);
    throw error;
  }
};

// Crear un nuevo agente
export const createAgent = async (
  agentData: Partial<ConversationalEngineAgent>
): Promise<ConversationalEngineAgent> => {
  try {
    const response = await api.post('/hvaagents/agents', agentData);
    return response.data.agent;
  } catch (error) {
    console.error('Error creating agent:', error);
    throw error;
  }
};

// Actualizar un agente existente
export const updateAgent = async (
  agentId: string,
  updates: Partial<ConversationalEngineAgent>
): Promise<ConversationalEngineAgent> => {
  try {
    const response = await api.put(`/hvaagents/agents/${agentId}`, updates);
    return response.data.agent;
  } catch (error) {
    console.error('Error updating agent:', error);
    throw error;
  }
};

// Cambiar el entorno de un agente
export const changeAgentEnvironment = async (
  agentId: string,
  request: ChangeEnvironmentRequest
): Promise<{
  success: boolean;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  agent?: any;
  replacedAgent?: {
    agentid: string;
    agentname: string;
    message: string;
  };
}> => {
  try {
    const response = await api.put(
      `/hvaagents/agents/${agentId}/environment`,
      request
    );
    return response.data;
  } catch (error) {
    console.error('Error changing agent environment:', error);
    throw error;
  }
};

// Verificar si una campaña tiene un agente en Production
export const checkCampaignAgent = async (
  campaignId: number
): Promise<{
  hasAgent: boolean;
  agent: { agentid: string; agentname: string; environment: string } | null;
}> => {
  try {
    const response = await api.get(`/hvaagents/campaign/${campaignId}/agent`);
    return response.data;
  } catch (error) {
    console.error('Error checking campaign agent:', error);
    throw error;
  }
};

// Eliminar un agente
export const deleteAgent = async (
  agentId: string
): Promise<{ success: boolean }> => {
  try {
    const response = await api.delete(`/hvaagents/agents/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting agent:', error);
    throw error;
  }
};

// ============ Funciones para Diagramas de Flujo ============
// Generar o regenerar diagrama para un agente
export const generateAgentDiagram = async (
  agentId: string,
  options?: GenerateDiagramRequest
): Promise<DiagramResponse> => {
  try {
    const params = new URLSearchParams();
    if (options?.force) params.append('force', 'true');
    if (options?.type) params.append('type', options.type);

    const response = await api.post(
      `/agents/${agentId}/diagram/generate`,
      {},
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating agent diagram:', error);
    throw error;
  }
};

// Obtener diagrama existente (sin generar nuevo)
export const getAgentDiagram = async (
  agentId: string,
  type: 'outbound' | 'inbound' = 'outbound'
): Promise<DiagramResponse> => {
  try {
    const response = await api.get(`/agents/${agentId}/diagram`, {
      params: { type }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching agent diagram:', error);
    throw error;
  }
};

// Subir diagrama editado manualmente
export const uploadAgentDiagram = async (
  agentId: string,
  data: UploadDiagramRequest
): Promise<{ success: boolean; message: string; prompt_type: string }> => {
  try {
    const response = await api.put(`/agents/${agentId}/diagram/upload`, data);
    return response.data;
  } catch (error) {
    console.error('Error uploading agent diagram:', error);
    throw error;
  }
};

// Eliminar diagrama
export const deleteAgentDiagram = async (
  agentId: string,
  type?: 'outbound' | 'inbound'
): Promise<{ success: boolean; message: string }> => {
  try {
    const params = type ? { type } : {};
    const response = await api.delete(`/agents/${agentId}/diagram`, {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting agent diagram:', error);
    throw error;
  }
};

// ============ Funciones para Configuraciones de adaptadores IA ============

// Configuraciones STT

export const getAllSttConfigs = async (): Promise<AIAdapterConfig[]> => {
  try {
    const response = await api.get('/hvaagents/config/stt');
    return response.data;
  } catch (error) {
    console.error('Error fetching STT configurations:', error);
    throw error;
  }
};

export const getSttConfigById = async (
  id: number
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.get(`/hvaagents/config/stt/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching STT configuration:', error);
    throw error;
  }
};

export const createSttConfig = async (
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.post('/hvaagents/config/stt', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating STT configuration:', error);
    throw error;
  }
};

export const updateSttConfig = async (
  id: number,
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.put(`/hvaagents/config/stt/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating STT configuration:', error);
    throw error;
  }
};

export const deleteSttConfig = async (id: number): Promise<void> => {
  try {
    await api.delete(`/hvaagents/config/stt/${id}`);
  } catch (error) {
    console.error('Error deleting STT configuration:', error);
    throw error;
  }
};

// Configuraciones LLM

export const getAllLlmConfigs = async (): Promise<AIAdapterConfig[]> => {
  try {
    const response = await api.get('/hvaagents/config/llm');
    return response.data;
  } catch (error) {
    console.error('Error fetching LLM configurations:', error);
    throw error;
  }
};

export const getLlmConfigById = async (
  id: number
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.get(`/hvaagents/config/llm/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching LLM configuration:', error);
    throw error;
  }
};

export const createLlmConfig = async (
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.post('/hvaagents/config/llm', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating LLM configuration:', error);
    throw error;
  }
};

export const updateLlmConfig = async (
  id: number,
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.put(`/hvaagents/config/llm/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating LLM configuration:', error);
    throw error;
  }
};

export const deleteLlmConfig = async (id: number): Promise<void> => {
  try {
    await api.delete(`/hvaagents/config/llm/${id}`);
  } catch (error) {
    console.error('Error deleting LLM configuration:', error);
    throw error;
  }
};

// Configuraciones TTS

export const getAllTtsConfigs = async (): Promise<AIAdapterConfig[]> => {
  try {
    const response = await api.get('/hvaagents/config/tts');
    return response.data;
  } catch (error) {
    console.error('Error fetching TTS configurations:', error);
    throw error;
  }
};

export const getTtsConfigById = async (
  id: number
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.get(`/hvaagents/config/tts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching TTS configuration:', error);
    throw error;
  }
};

export const createTtsConfig = async (
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.post('/hvaagents/config/tts', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating TTS configuration:', error);
    throw error;
  }
};

export const updateTtsConfig = async (
  id: number,
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.put(`/hvaagents/config/tts/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating TTS configuration:', error);
    throw error;
  }
};

export const deleteTtsConfig = async (id: number): Promise<void> => {
  try {
    await api.delete(`/hvaagents/config/tts/${id}`);
  } catch (error) {
    console.error('Error deleting TTS configuration:', error);
    throw error;
  }
};

// Configuraciones para resúmenes y análisis

export const getAllSummaryConfigs = async (): Promise<AIAdapterConfig[]> => {
  try {
    const response = await api.get('/hvaagents/config/summary');
    return response.data;
  } catch (error) {
    console.error('Error fetching Summary configurations:', error);
    throw error;
  }
};

export const getSummaryConfigById = async (
  id: number
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.get(`/hvaagents/config/summary/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Summary configuration:', error);
    throw error;
  }
};

export const createSummaryConfig = async (
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.post('/hvaagents/config/summary', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating Summary configuration:', error);
    throw error;
  }
};

export const updateSummaryConfig = async (
  id: number,
  data: Partial<AIAdapterConfig>
): Promise<AIAdapterConfig> => {
  try {
    const response = await api.put(`/hvaagents/config/summary/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Error updating Summary configuration:', error);
    throw error;
  }
};

export const deleteSummaryConfig = async (id: number): Promise<void> => {
  try {
    await api.delete(`/hvaagents/config/summary/${id}`);
  } catch (error) {
    console.error('Error deleting Summary configuration:', error);
    throw error;
  }
};

// ============ Funciones para Clientes Reales de Testing ============

export const getRealCustomersForTesting = async (
  params: GetRealCustomersParams = {}
): Promise<PaginatedCustomersResponse> => {
  try {
    const { page = 1, pageSize = 20, search = '' } = params;

    const response = await api.get('/hvaagents/real-customers', {
      params: {
        page,
        pageSize,
        search
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching real customers for testing:', error);
    throw error;
  }
};
